import * as React from 'react'
import {
  IonContent,
  IonButton,
  IonPage,
  IonRow,
  IonLabel,
  IonCard,
  IonCardContent,
  IonItem,
  IonInput,
  IonTextarea,
  IonCol,
  IonImg,
  IonHeader,
  IonLoading,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  useIonAlert,
} from '@ionic/react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import {
  addYears,
  isSameDay,
  parseISO,
  subYears as subtractYears,
} from 'date-fns'

import avatar from '../../resources/avatar-placeholder.png'
import * as commonStrings from '../../utils/common-strings'
import {
  hasTitleArray,
  hasVendorArray,
  hasTrackingNumberArray,
} from '../../constants/classConstants'
import Header from '../../components/header'
import AddFab from '../../components/fab/add-fab'
import { useUpdateTask } from '../../hooks/tasks/mutations'
import { useTaskDetail } from '../../hooks/tasks/queries'
import ClientDetailHeader from '../../components/client/ClientDetailHeader'
import { useUser } from '../../contexts/authContext'
import DatePicker from '../../components/ui/ionic/date-picker'
import type { TaskClass } from '../../hooks/tasks/queries'

const debounceTime = 500
const today = new Date()

const getTypeString = (taskType: string) => {
  switch (taskType) {
    case 'SpecialOrder':
      return 'SPECIAL ORDER'
    case 'FollowUp':
      return 'TASK'
    default:
      return taskType
  }
}

function TaskDetail() {
  const { url } = useRouteMatch()
  const history = useHistory()
  const { objectId, className } = useParams<{
    objectId: string
    className: TaskClass
  }>()
  const user = useUser()

  const [presentAutomationAlert] = useIonAlert()

  const taskQuery = useTaskDetail({ objectId, className })
  const updateTask = useUpdateTask()
  const {
    control,
    getValues,
    resetField,
    formState: { dirtyFields },
  } = useForm()

  // Debounce the udpate mutation
  const debouncedTaskUpdate = useDebouncedCallback(
    // function
    () => {
      // account for canceled completed at (and no initial data)
      if (Object.keys(dirtyFields).length === 0) return

      updateTask.mutate({
        ...getValues(),
        objectId,
        className,
      })
    },
    // delay in ms
    debounceTime
  )

  const onSendComplete = async () => {
    history.push('/NextContact/' + taskQuery.data?.client.objectId)
  }

  if (taskQuery.data) {
    return (
      <IonPage>
        <AddFab />
        <IonHeader>
          <Header
            title={getTypeString(className)}
            backRef={commonStrings.backTasks}
          />
        </IonHeader>
        <IonContent>
          <ClientDetailHeader
            client={taskQuery.data.client}
            mode="dark"
            onCommunicationComplete={onSendComplete}
          />

          <IonCard color="secondary">
            <IonCardHeader>
              <IonCardTitle>{getTypeString(className)}</IonCardTitle>
            </IonCardHeader>

            <IonCardContent>
              <form>
                <IonItem
                  color="secondary"
                  disabled={taskQuery.data.task.user.objectId !== user.objectId}
                >
                  <IonLabel>Private</IonLabel>
                  <Controller
                    control={control}
                    name="isPublic"
                    defaultValue={!taskQuery.data.task.isPublic}
                    render={({ field: { value, onChange } }) => (
                      <IonCheckbox
                        checked={value}
                        onIonChange={(e) => {
                          onChange(e.detail.checked)
                          debouncedTaskUpdate()
                        }}
                      />
                    )}
                  />
                </IonItem>

                {hasTitleArray.includes(className) && (
                  <IonItem color="secondary">
                    <IonLabel position="floating">Title</IonLabel>
                    <Controller
                      control={control}
                      name="title"
                      defaultValue={taskQuery.data.task.title}
                      render={({ field: { value, onChange } }) => (
                        <IonInput
                          color="primary"
                          value={value}
                          onIonChange={(e) => {
                            onChange(e.detail.value)
                            debouncedTaskUpdate()
                          }}
                        />
                      )}
                    />
                  </IonItem>
                )}

                {hasVendorArray.includes(className) && (
                  <IonItem color="secondary">
                    <IonLabel position="floating">Vendor</IonLabel>
                    <Controller
                      control={control}
                      name="vendor"
                      defaultValue={taskQuery.data.task.vendor}
                      render={({ field: { value, onChange } }) => (
                        <IonInput
                          color="primary"
                          value={value}
                          onIonChange={(e) => {
                            onChange(e.detail.value)
                            debouncedTaskUpdate()
                          }}
                        />
                      )}
                    />
                  </IonItem>
                )}
                {hasTrackingNumberArray.includes(className) && (
                  <IonItem color="secondary">
                    <IonLabel position="floating">Tracking</IonLabel>
                    <Controller
                      control={control}
                      name="trackingNumber"
                      defaultValue={taskQuery.data.task.trackingNumber}
                      render={({ field: { value, onChange } }) => (
                        <IonInput
                          value={value}
                          onIonChange={(e) => {
                            onChange(e.detail.value)
                            debouncedTaskUpdate()
                          }}
                        />
                      )}
                    />
                  </IonItem>
                )}

                <IonItem color="secondary">
                  <IonLabel position="floating">Note</IonLabel>
                  <Controller
                    control={control}
                    name="note"
                    defaultValue={taskQuery.data.task.note ?? ''}
                    render={({ field: { value, onChange } }) => (
                      <IonTextarea
                        autoGrow
                        autocapitalize="on"
                        value={value}
                        onIonChange={(e) => {
                          onChange(e.detail.value)
                          debouncedTaskUpdate()
                        }}
                      />
                    )}
                  />
                </IonItem>
                <IonItem color="secondary">
                  <IonLabel>Complete</IonLabel>
                  <Controller
                    control={control}
                    name="completedAt"
                    defaultValue={Boolean(taskQuery.data.task.completedAt)}
                    render={({ field: { value, onChange } }) => (
                      <IonCheckbox
                        disabled={updateTask.isLoading}
                        checked={value}
                        onIonChange={(e) => {
                          const { checked } = e.detail
                          onChange(checked)

                          if (checked && className === 'Alteration') {
                            presentAutomationAlert({
                              header: 'What would you like to do?',
                              buttons: [
                                {
                                  text: 'Cancel',
                                  cssClass: 'px-3 text-ion-color-secondary',
                                  role: 'cancel',
                                  handler: () =>
                                    resetField('completedAt', {
                                      keepDirty: false,
                                    }),
                                },
                                {
                                  text: 'Complete without starting automation',
                                  cssClass: 'px-3 text-ion-color-secondary',
                                  role: 'default',
                                  handler: () => {
                                    updateTask.mutate({
                                      completedAt: true,
                                      objectId,
                                      className,
                                    })
                                  },
                                },
                                {
                                  text: 'Complete and start automation',
                                  cssClass: 'px-3',
                                  role: 'confirm',
                                  handler: () => {
                                    updateTask.mutate({
                                      completedAt: true,
                                      trigger: true,
                                      objectId,
                                      className,
                                    })
                                  },
                                },
                              ],
                            })
                          } else if (taskQuery.data?.task.trigger && !checked) {
                            presentAutomationAlert({
                              header:
                                'You are about to uncomplete and stop any automations. Do you want to proceed?',
                              buttons: [
                                {
                                  text: 'Cancel',
                                  cssClass: 'px-3 text-ion-color-secondary',
                                  role: 'cancel',
                                  handler: () =>
                                    resetField('completedAt', {
                                      defaultValue: true,
                                      keepDirty: false,
                                    }),
                                },
                                {
                                  text: 'OK',
                                  cssClass: 'px-3',
                                  role: 'confirm',
                                  handler: () => {
                                    updateTask.mutate({
                                      completedAt: false,
                                      trigger: false,
                                      objectId,
                                      className,
                                    })
                                  },
                                },
                              ],
                            })
                          } else {
                            debouncedTaskUpdate()
                          }
                        }}
                      />
                    )}
                  />
                </IonItem>

                <Controller
                  control={control}
                  name="due"
                  defaultValue={taskQuery.data.task.due?.iso ?? ''}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      label="Due Date"
                      fallback="No Due Date set."
                      value={value}
                      min={subtractYears(today, 1)}
                      max={addYears(today, 1)}
                      onChange={(e) => {
                        onChange(e.detail.value)
                        // no due date set yet
                        if (!taskQuery.data.task.due) debouncedTaskUpdate()

                        // compare due date and calendar date to update
                        if (
                          taskQuery.data.task.due &&
                          typeof e.detail.value === 'string'
                        ) {
                          if (
                            !isSameDay(
                              parseISO(taskQuery.data.task.due.iso),
                              new Date(e.detail.value)
                            )
                          ) {
                            debouncedTaskUpdate()
                          }
                        }
                      }}
                    />
                  )}
                />

                <IonRow class="ion-justify-content-center">
                  {taskQuery.data?.task.photos
                    ? taskQuery.data?.task.photos.map((photo, index) => (
                        <IonCol
                          size="6"
                          key={index}
                          class="ion-justify-content-center"
                        >
                          <IonImg
                            src={photo.url || avatar}
                            alt={commonStrings.associateAddedImage}
                          />
                        </IonCol>
                      ))
                    : null}
                </IonRow>
              </form>
            </IonCardContent>
          </IonCard>

          {taskQuery.data.task.client ? (
            <IonRow class="ion-justify-content-center ion-padding-bottom ion-margin-bottom">
              <IonButton
                color="secondary"
                routerLink={`${url}/client/${taskQuery.data?.task.client.objectId}`}
              >
                GO TO PROFILE
              </IonButton>
            </IonRow>
          ) : null}
        </IonContent>
      </IonPage>
    )
  }
  return (
    <IonPage>
      <IonLoading
        isOpen={taskQuery.isLoading}
        message="Loading Task Details..."
      />
    </IonPage>
  )
}

export default TaskDetail
